import {Box, chakra, Icon, Text} from '@chakra-ui/react'
import {Batch} from '@cypro/engine/schema'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {AccessorColDef, DisplayColDef} from '@cypro/ui/types'
import {getShortDateFormatterUTC} from '@cypro/ui/utils'
import {createColumnHelper} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {LuCircleAlert, LuTrash} from 'react-icons/lu'
import {getBatchDaysLeft, isBatchDisposed, isBatchExpiringSoon} from '@/modules/stocks/functions'

export const batchesColumnHelper = createColumnHelper<Batch>()

type AccessorColumnDef = AccessorColDef<Batch>
type DisplayColumnDef = DisplayColDef<Batch>

export const BatchesColumnsDef = {
  lotNumber: (def?: AccessorColumnDef) =>
    batchesColumnHelper.accessor('lotNumber', {
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="flex-start" width="100%">
            {t('batch')}
          </chakra.div>
        )
      },
      cell: ({row}) => {
        const {t} = useTranslate('common')

        const {color, isExpired, isExpiringSoon} = getExpiryMeta(row.original.expiryDate)
        const expiryColor = isExpired || isExpiringSoon ? color : null
        const expiryTooltip = isExpired ? t('expired_open_drawer_to_dispose') : t('expiry_soon')
        const disposed = isBatchDisposed(row.original)

        return (
          <chakra.div display="flex" alignItems="center" justifyContent="flex-start" width="100%" gap={2}>
            {!disposed && isExpired && expiryColor && expiryTooltip && (
              <Tooltip content={expiryTooltip}>
                <Icon as={LuCircleAlert} color={expiryColor} boxSize={4} />
              </Tooltip>
            )}
            {disposed && (
              <Tooltip content={t('disposed')}>
                <Box color="fg.error">
                  <LuTrash size={10} />
                </Box>
              </Tooltip>
            )}
            <Tooltip content={row.original.lotNumber} disabled={!row.original.lotNumber}>
              <Text lineClamp={1}>{row.original.lotNumber}</Text>
            </Tooltip>
          </chakra.div>
        )
      },
      ...def,
    }),

  expiryDate: (def?: DisplayColumnDef) =>
    batchesColumnHelper.display({
      id: 'expiryDate',
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="start" width="100%">
            {t('expiry_date')}
          </chakra.div>
        )
      },
      cell: ({row}) => {
        const {color} = getExpiryMeta(row.original.expiryDate)

        return (
          <chakra.div display="flex" color={color} justifyContent="start" width="100%">
            {getShortDateFormatterUTC().format(row.original.expiryDate)}
          </chakra.div>
        )
      },
      ...def,
    }),

  supplierCompany: (def?: AccessorColumnDef) =>
    batchesColumnHelper.accessor('supplierCompany.name', {
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="flex-start" width="100%">
            {t('supplier')}
          </chakra.div>
        )
      },
      cell: ({row}) => (
        <chakra.div display="flex" justifyContent="flex-start" width="100%">
          <Tooltip content={row.original.supplierCompany?.name} disabled={!row.original.supplierCompany?.name}>
            <Text lineClamp={1}>{row.original.supplierCompany?.name || '-'}</Text>
          </Tooltip>
        </chakra.div>
      ),
      ...def,
    }),

  stockLocation: (def?: AccessorColumnDef) =>
    batchesColumnHelper.accessor('stockLocation.name', {
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="flex-start" width="100%">
            {t('location')}
          </chakra.div>
        )
      },
      cell: ({row}) => (
        <chakra.div display="flex" justifyContent="flex-start" width="100%">
          <Tooltip content={row.original.stockLocation?.name} disabled={!row.original.stockLocation?.name}>
            <Text lineClamp={1}>{row.original.stockLocation?.name || '-'}</Text>
          </Tooltip>
        </chakra.div>
      ),
      ...def,
    }),

  stock: (def?: DisplayColumnDef) =>
    batchesColumnHelper.display({
      id: 'stock',
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="flex-end" width="100%">
            {t('stock')}
          </chakra.div>
        )
      },
      cell: ({row}) => {
        return (
          <chakra.div
            display="flex"
            justifyContent="flex-end"
            width="100%"
          >{`${row.original.quantity}/${row.original.receivedQuantity}`}</chakra.div>
        )
      },
      ...def,
    }),
}

function getExpiryMeta(expiryDate: Date | number | null) {
  // a batch is usable through its expiry day, so today is not expired (CY-371)
  const days = expiryDate === null ? null : getBatchDaysLeft(expiryDate)
  const isExpired = days !== null && days <= 0
  const isExpiringSoon = expiryDate !== null && isBatchExpiringSoon(expiryDate)

  const color = isExpired ? 'fg.red' : isExpiringSoon ? 'fg.warning' : 'fg'

  return {days, isExpired, isExpiringSoon, color}
}
